/**
 * Type re-exports from the generated OpenAPI contract (APP-001 §10).
 *
 * Feature modules import DTOs from here rather than the deep generated path.
 * This keeps the generated boundary stable: if the contract field names
 * change, these aliases surface the drift at the import site. The generated
 * file (`./generated/openapi.d.ts`) is never edited directly.
 */
import type { components } from './generated/openapi';

type Schema<K extends keyof components['schemas']> = components['schemas'][K];

// Dashboard identity / account bootstrap (APP-001 §9)
export type DashboardIdentityResponse = Schema<'DashboardIdentityResponse'>;
export type DashboardAccount = Schema<'DashboardAccount'>;
export type AccountResponse = Schema<'AccountResponse'>;
export type AccountPatchRequest = Schema<'AccountPatchRequest'>;

// Overview (APP-001 §18)
export type OverviewResponse = Schema<'OverviewResponse'>;

// Usage
export type UsageSummaryResponse = Schema<'UsageSummaryResponse'>;
export type UsageHistoryResponse = Schema<'UsageHistoryResponse'>;
export type UsageHistoryItem = UsageHistoryResponse['items'][number];
export type UsageHistoryGranularity = UsageHistoryResponse['granularity'];
export type ListMetadata = Schema<'ListMetadata'>;

// API keys
export type APIKeyMetadata = Schema<'APIKeyMetadata'>;
export type APIKeyEnvironment = Schema<'APIKeyEnvironment'>;
export type APIKeyStatus = Schema<'APIKeyStatus'>;
export type APIKeyScope = Schema<'APIKeyScope'>;
export type APIKeyListResponse = Schema<'APIKeyListResponse'>;
export type APIKeyCreateRequest = Schema<'APIKeyCreateRequest'>;
export type APIKeyCreatedResponse = Schema<'APIKeyCreatedResponse'>;
export type APIKeyPatchRequest = Schema<'APIKeyPatchRequest'>;

// Billing
export type BillingSummary = Schema<'BillingSummary'>;
export type CheckoutRequest = Schema<'CheckoutRequest'>;
export type CheckoutResponse = Schema<'CheckoutResponse'>;
export type PortalResponse = Schema<'PortalResponse'>;
// In-app plan change (existing active/trialing subscription; no hosted flow).
export type PlanChangeRequest = Schema<'PlanChangeRequest'>;
export type PlanChangeResponse = Schema<'PlanChangeResponse'>;
export type PlanChangeOutcome = Schema<'PlanChangeOutcome'>;
/** Pending period-end switch carried on BillingSummary/overview billing block. */
export type ScheduledPlanChange = NonNullable<BillingSummary['scheduled_change']>;
export type BillingInterval = Schema<'BillingInterval'>;
export type SubscriptionStatus = Schema<'SubscriptionStatus'>;
export type AccessState = Schema<'AccessState'>;

// Bulk + webhook summaries (dashboard)
export type BulkJobSummaryResponse = Schema<'BulkJobSummaryResponse'>;
export type BulkJobListResponse = Schema<'BulkJobListResponse'>;
// APP-007 — full bulk create/status DTOs (Clerk-session BFF surfaces).
export type BulkJobStatus = Schema<'BulkJobStatus'>;
export type BulkJobCreateResponse = Schema<'BulkJobCreateResponse'>;
export type BulkJobStatusResponse = Schema<'BulkJobStatusResponse'>;
export type BulkWarning = Schema<'Warning'>;
export type WebhookSummaryResponse = Schema<'WebhookSummaryResponse'>;
export type WebhookListResponseDashboard = Schema<'WebhookListResponseDashboard'>;
// APP-008 — full webhook management DTOs (Clerk-session BFF surfaces). The
// summary types above are the read-only dashboard list; these are the canonical
// config/delivery shapes used by create/get/patch/rotate/deliveries/test.
export type WebhookEventType = Schema<'WebhookEventType'>;
export type WebhookConfigResponse = Schema<'WebhookConfigResponse'>;
export type WebhookCreateRequest = Schema<'WebhookCreateRequest'>;
export type WebhookUpdateRequest = Schema<'WebhookUpdateRequest'>;
export type WebhookSingleResponse = Schema<'WebhookSingleResponse'>;
export type WebhookDeliveryStatus = Schema<'WebhookDeliveryStatus'>;
export type WebhookDeliveryResponse = Schema<'WebhookDeliveryResponse'>;
export type WebhookDeliveriesResponse = Schema<'WebhookDeliveriesResponse'>;
export type WebhookTestDeliveryResponse = Schema<'WebhookTestDeliveryResponse'>;
export type WebhookWarning = Schema<'Warning'>;

// Playground
export type PlaygroundRequest = Schema<'PlaygroundRequest'>;
export type PlaygroundResponse = Schema<'PlaygroundResponse'>;

// Analysis result sub-types. The generated PlaygroundResponse.result is typed
// `unknown` (the dashboard package marshals the full EmailAnalysisResponse raw
// into the envelope — APP-005 §1). Feature modules narrow it to these types
// instead of duplicating the contract by hand (APP-005 §47).
export type EmailInfo = Schema<'EmailInfo'>;
export type EmailAnalysisResponse = Schema<'EmailAnalysisResponse'>;
export type Decision = Schema<'Decision'>;
export type DecisionAction = Decision['action'];
export type RiskBand = Decision['risk_band'];
export type Assessment = Schema<'Assessment'>;
export type AssessmentLimitation = Assessment['limitations'][number];
export type Checks = Schema<'Checks'>;
export type CheckDisposableEmail = NonNullable<Checks['disposable_email']>;
export type CheckDomainInfrastructure = NonNullable<Checks['domain_infrastructure']>;
export type CheckProvider = NonNullable<Checks['provider']>;
export type CheckRoleAddress = NonNullable<Checks['role_address']>;
export type CheckDomainTypo = NonNullable<Checks['domain_typo']>;
export type CheckSyntheticIdentity = NonNullable<Checks['synthetic_identity']>;
export type CheckNameMatch = NonNullable<Checks['name_match']>;
export type CheckIPReputation = NonNullable<Checks['ip_reputation']>;
export type DomainInfraRecord = Schema<'DomainInfraRecord'>;
export type NameMatchSubCheck = Schema<'NameMatchSubCheck'>;
export type EvidenceItem = Schema<'EvidenceItem'>;
export type RiskContribution = Schema<'RiskContribution'>;
export type DisposableClassification = NonNullable<NonNullable<CheckDisposableEmail['result']>>;
export type Explanation = Schema<'Explanation'>;
export type TopReasonEntry = NonNullable<Explanation['top_reasons']>[number];
export type ReasonGroups = NonNullable<Explanation['reason_groups']>;
export type RiskFactor = NonNullable<Explanation['risk_factors']>[number];
export type Warning = Schema<'Warning'>;
export type ResponseMetadata = Schema<'ResponseMetadata'>;
export type UsageSnapshot = Schema<'UsageSnapshot'>;
export type UsageDimension = Schema<'UsageDimension'>;
export type PlanContext = Schema<'PlanContext'>;
export type BillingPeriod = Schema<'BillingPeriod'>;
export type CacheInfo = Schema<'CacheInfo'>;

// Public checker (anonymous, used by marketing surface only; typed here for
// completeness but NOT authenticated by the dashboard client).
export type PublicCheckerRequest = Schema<'PublicCheckerRequest'>;
export type PublicCheckerResponse = Schema<'PublicCheckerResponse'>;

// Public playground (anonymous pre-signup playground at /public/playground).
// Same `result` narrowing as the authenticated playground: the generated
// PublicPlaygroundResponse.result is typed `unknown` because the server
// marshals the full EmailAnalysisResponse raw into the envelope.
export type PublicPlaygroundRequest = Schema<'PublicPlaygroundRequest'>;
export type PublicPlaygroundResponse = Schema<'PublicPlaygroundResponse'>;

// Plan catalog derived values used for frontend-safe allowlists.
// Self-service plans only (Developer/Growth/Scale/Business) — Enterprise is
// contact-sales. See docs/api/PLAN_QUOTA_REFERENCE.md and
// docs/features/PRICING-2026-09-live-site-alignment.md. These are display
// hints only; the backend remains the plan authority.
export const SELF_SERVICE_PLANS = ['Developer', 'Growth', 'Scale', 'Business'] as const;
export type SelfServicePlan = (typeof SELF_SERVICE_PLANS)[number];

export const BILLING_INTERVALS = ['monthly', 'annual'] as const;
export type BillingIntervalValue = (typeof BILLING_INTERVALS)[number];
